import { useEffect, useState } from "react";
import { theme, type as text } from "../theme";
import { api } from "../api/client";
import { DetailSection } from "./RecordView";
import { PortalLink } from "./PortalLink";
import { StatusTag } from "./Drawing";
import { fmtDateTime } from "../lib/format";

// Recent changes beside an incident (backend GET /changes/for-incident/:id, lib/changeRegister.ts
// recentChangesForIncident): the changes made to the incident's assets, or to what they rest on, shortly
// before or during it. A cross-reference for whoever investigates, never a finding that a change caused it.

interface RecentChange {
  id: string; ref: string; title: string; changeType: string; status: string; implementedAt: string;
  timing: "before" | "during";
  direct: Array<{ id: string; name: string }>;
  viaDependency: Array<{ id: string; name: string }>;
}
interface Response { changes: RecentChange[]; lookbackDays: number; assetCount: number }

export function IncidentRecentChanges({ incidentId, refreshKey }: { incidentId: string; refreshKey?: unknown }) {
  const [data, setData] = useState<Response | null>(null);

  useEffect(() => {
    let live = true;
    api.get<Response>(`/changes/for-incident/${encodeURIComponent(incidentId)}`)
      .then((d) => { if (live) setData(d); })
      .catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [incidentId, refreshKey]);

  if (!data) return null;
  return (
    <DetailSection title="Recent changes to these assets">
      <div style={{ display: "grid", gap: 10 }}>
        {data.assetCount === 0 ? (
          <div style={{ ...text.hint }}>Link the assets this incident touched to see the changes made to them in the {data.lookbackDays} days before it.</div>
        ) : data.changes.length === 0 ? (
          <div style={{ ...text.hint }}>No change was recorded against these assets, or what they rest on, in the {data.lookbackDays} days before the incident or while it ran.</div>
        ) : (
          <>
            <div style={text.hint}>
              Changes made in the {data.lookbackDays} days before the incident began, or while it ran, to its assets or to what they rest on.
              A lead to follow, not a finding of cause.
            </div>
            {data.changes.map((c) => (
              <div key={c.id} style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap", ...text.body }}>
                <StatusTag tone={c.timing === "during" ? "partial" : "none"}>{c.timing === "during" ? "During" : "Before"}</StatusTag>
                <span style={{ flex: 1, minWidth: 240 }}>
                  <PortalLink to={`/changes?change=${c.id}`} style={{ color: "var(--c-accentText)" }}>
                    <span style={{ ...text.ref, marginRight: 6 }}>{c.ref}</span>{c.title}
                  </PortalLink>
                  <span style={{ ...text.hint, color: theme.color.muted }}>
                    {" "}· {c.changeType === "emergency" ? "emergency, " : ""}made {fmtDateTime(c.implementedAt)}
                    {c.direct.length > 0 && <> · touched {c.direct.map((a) => a.name).join(", ")}</>}
                    {c.viaDependency.length > 0 && <> · touched {c.viaDependency.map((a) => a.name).join(", ")}, which they rest on</>}
                  </span>
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </DetailSection>
  );
}
